import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon, LeafIcon } from 'lucide-react';
import { collections, stories, bestSellers, newArrivals, giftThemes, sizes } from '../data/mockData';
import { stockistRegions } from '../data/siteContent';
import { SectionHeading } from '../components/SectionHeading';
import { RangeCard } from '../components/RangeCard';
import { ProductCard } from '../components/ProductCard';
import { CardCarousel } from '../components/CardCarousel';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Home() {
  const featured = [...bestSellers, ...newArrivals].slice(0, 4);
  return <div className="w-full bg-paper" data-id="element-467" data-mp="2dhgu19o7c8d9">
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative w-full overflow-hidden bg-paper-warm" data-id="element-468" data-mp="3kvtmpi7mzxzj">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 py-16 lg:grid-cols-[1.05fr_1fr] lg:gap-20 lg:py-24" data-id="element-469" data-mp="3owvni3h7ci3m">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: EASE
        }} data-id="element-470" data-mp="30x1jeg8b2qr1">
            <p className="type-eyebrow mb-7 text-[11px] text-pennant" data-id="element-471" data-mp="2qybx8b9dmk4z">
              Ecoffee Cup<sup className="align-super" data-id="element-472" data-mp="2r0gctqgn17l3">®</sup> South Africa
            </p>
            <h1 className="type-display text-6xl text-coffee sm:text-7xl lg:text-[5.5rem]" data-id="element-473" data-mp="2mwqrraz6984t">
              The cup you
              <br data-id="element-474" data-mp="2hzqlayyeu3d6" />
              actually want
              <br data-id="element-475" data-mp="2d65srmfl61ld" />
              to carry.
            </h1>
            <p className="type-body mt-8 max-w-md text-lg text-ink/65" data-id="element-476" data-mp="28841ie80avyt">
              Bamboo fibre cups in three sizes, printed with the work of
              artists we love — and with stories drawn from the Karoo, the Cape
              and the fynbos.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3" data-id="element-477" data-mp="1z6mpb6xpzefe">
              <Link to="/shop" className="type-cta rounded-full bg-coffee px-9 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-478" data-mp="1u9ntmjyeqzf3">
                Shop now
              </Link>
              <Link to="/size-guide" className="type-cta rounded-full border border-coffee/25 px-9 py-4 text-[11px] text-coffee transition-colors hover:border-coffee hover:bg-coffee hover:text-paper" data-id="element-479" data-mp="1ok7wkjz33vox">
                Size guide
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-10 gap-y-4" data-id="element-480" data-mp="28abimxtvn44n">
              {sizes.map(s => <div key={s.id} data-id="element-481" data-mp="1tlf4qk552ww4">
                  <p className="type-display text-2xl text-coffee" data-id="element-482" data-mp="1tkdqy7wqnwap">
                    {s.label}
                  </p>
                  <p className="type-body text-xs text-ink/45" data-id="element-483" data-mp="1yixeuduw7i5p">
                    {s.drinks[0]}
                  </p>
                </div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.1,
          ease: EASE
        }} className="relative" data-id="element-484" data-mp="2r2dsnqc0jm5x">
            <div className="overflow-hidden rounded-[2.5rem]" data-id="element-485" data-mp="akwk2rnnrcgu">
              <img src="https://images.unsplash.com/photo-1511537190424-bbbab87ac5eb?auto=format&fit=crop&q=80&w=1400" alt="An Ecoffee Cup on a stoep table in the early morning" className="h-[52vh] w-full object-cover lg:h-[64vh]" data-id="element-486" data-mp="odm8t3ukmvhn" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── DISCOVERY 1 · CHOOSE YOUR STORY (South African) ──── */}
      <section className="w-full bg-pennant py-20 md:py-28" data-id="element-487" data-mp="5l9xvspuqlvw">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-488" data-mp="nrz2v5jtn1m">
          <SectionHeading tone="light" eyebrow="Choose Your Story" title="Ranges drawn from South Africa." subtitle="Our local story layer — places and moods from this country, made with artists who live here. Start with the one that sounds like your morning." action={<Link to="/stories" className="type-cta inline-flex items-center gap-2 rounded-full border border-paper/30 px-7 py-3.5 text-[11px] text-paper transition-colors hover:bg-paper hover:text-pennant" data-id="element-490" data-mp="2ce11ofohy9om">
                All stories
                <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-491" data-mp="2qdiv58cqmb4b" />
              </Link>} data-id="element-489" data-mp="1h2je86dooxsz" />
          <CardCarousel tone="dark" className="mt-14" data-id="element-492" data-mp="2vaj1lkdi1fvy">
            {stories.map((s, i) => <RangeCard key={s.id} range={s} index={i} data-id="element-493" data-mp="303veix8tfy8v" data-mpi={i} />)}
          </CardCarousel>
        </div>
      </section>

      {/* ── FEATURED PRODUCTS ────────────────────────────────── */}
      <section className="w-full py-20 md:py-28" data-id="element-494" data-mp="3a1rmxhadcf3a">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-495" data-mp="39yx6e0uh7ef5">
          <SectionHeading eyebrow="Bestsellers" title="The ones that keep leaving." action={<Link to="/shop" className="type-cta inline-flex items-center gap-2 text-[11px] text-coffee underline decoration-oat underline-offset-[6px] hover:decoration-coffee" data-id="element-497" data-mp="12cvml4s0qtos">
                Shop all cups
              </Link>} data-id="element-496" data-mp="3e34wa1co70ey" />
          <CardCarousel className="mt-14" data-id="element-498" data-mp="ohbc8xqcvjsz">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} data-id="element-499" data-mp="1m25zyc4al2jq" data-mpi={i} />)}
          </CardCarousel>
        </div>
      </section>

      {/* ── DISCOVERY 2 · OFFICIAL COLLECTIONS ───────────────── */}
      <section className="w-full bg-paper-warm py-20 md:py-28" data-id="element-500" data-mp="1cbbg1eeiqf1g">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-501" data-mp="17d1iix5bn2co">
          <SectionHeading eyebrow="Our Collections" title="The official artist &amp; heritage ranges." subtitle="Licensed collections from the world’s great design archives — William Morris, the Van Gogh Museum, Emma Shipley and Kew Gardens." action={<Link to="/collections" className="type-cta inline-flex items-center gap-2 rounded-full border border-coffee/25 px-7 py-3.5 text-[11px] text-coffee transition-colors hover:bg-coffee hover:text-paper" data-id="element-503" data-mp="26s6rijj5w59">
                All collections
                <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-504" data-mp="3phj5hxfaqgpo" />
              </Link>} data-id="element-502" data-mp="17c8i0dwyh92z" />
          <CardCarousel className="mt-14" data-id="element-505" data-mp="3flg6h0fqqy6y">
            {collections.map((c, i) => <RangeCard key={c.id} range={c} index={i} data-id="element-506" data-mp="31lwxy6j0pku4" data-mpi={i} />)}
          </CardCarousel>
        </div>
      </section>

      {/* ── SIZE & DRINK GUIDE ───────────────────────────────── */}
      <section className="w-full py-20 md:py-28" data-id="element-507" data-mp="zuk5igy8l5ey">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-508" data-mp="zvv9ohvlgf6l">
          <div className="grid items-center gap-10 overflow-hidden rounded-[2.5rem] bg-coffee lg:grid-cols-2" data-id="element-509" data-mp="14tn2rf7z67cd">
            <div className="p-10 md:p-16" data-id="element-510" data-mp="1ex223olo69r2">
              <p className="type-eyebrow mb-6 text-[11px] text-paper/55" data-id="element-511" data-mp="1ey3bzpqy8q77">
                Size &amp; Drink Guide
              </p>
              <h2 className="type-display text-4xl text-paper md:text-5xl" data-id="element-512" data-mp="19wnvmazpv7v5">
                Three sizes. One of them is yours.
              </h2>
              <p className="type-body mt-6 max-w-md text-base text-paper/70" data-id="element-513" data-mp="1iy42w9v8itv5">
                250ml for the short and strong, 350ml for the everyday flat
                white, 400ml for the long mornings. The guide sets all three
                out beside the drinks they suit.
              </p>
              <Link to="/size-guide" className="type-cta mt-9 inline-flex items-center gap-2 rounded-full bg-paper px-9 py-4 text-[11px] text-coffee transition-colors hover:bg-oat" data-id="element-514" data-mp="26nq3n1r99h59">
                Read the guide
                <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-515" data-mp="894xwllbyc5u" />
              </Link>
            </div>
            <div className="h-full" data-id="element-516" data-mp="wul8ia19w6pa">
              <img src="https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&q=80&w=1200" alt="Three cup sizes side by side" className="h-64 w-full object-cover lg:h-full" data-id="element-517" data-mp="wta1m1cj6ity" />
            </div>
          </div>
        </div>
      </section>

      {/* ── GIFTING TEASER ───────────────────────────────────── */}
      <section className="w-full bg-paper-warm py-20 md:py-28" data-id="element-518" data-mp="dwine6buitwr">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-519" data-mp="itsahfbrithf">
          <SectionHeading eyebrow="Gifting" title="A gift that gets used every day." subtitle="Themed ideas for one person, with no minimum order." action={<Link to="/gifting" className="type-cta inline-flex items-center gap-2 rounded-full bg-coffee px-7 py-3.5 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-521" data-mp="rsvviouhpiyk">
                Explore gifting
              </Link>} data-id="element-520" data-mp="iuk4wmtmil64" />
          <CardCarousel cols={3} className="mt-14" data-id="element-522" data-mp="2dfwvvwt5slr3">
            {giftThemes.slice(0, 3).map((g, i) => <motion.div key={g.id} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06,
            ease: EASE
          }} data-id="element-523" data-mp="28hbwu0pn8848" data-mpi={i}>
                <Link to="/gifting" className="group block" data-id="element-524" data-mp="1pohqfv635obm">
                  <div className="overflow-hidden rounded-4xl" data-id="element-525" data-mp="1pmwwo7rli9et">
                    <img src={g.image} alt={g.title} className="aspect-[5/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" data-id="element-526" data-mp="1komz5qieewq1" />
                  </div>
                  <h3 className="type-display mt-5 text-2xl text-coffee" data-id="element-527" data-mp="3b23yx29te61c">
                    {g.title}
                  </h3>
                  <p className="type-body mt-1 text-sm text-ink/60" data-id="element-528" data-mp="cntkxyefyo27">
                    {g.blurb}
                  </p>
                </Link>
              </motion.div>)}
          </CardCarousel>

          <div className="mt-12 flex flex-col items-start gap-4 border-t border-oat pt-8 md:flex-row md:items-center md:justify-between" data-id="element-529" data-mp="mlptqjjwp6x5">
            <p className="type-body max-w-xl text-sm text-ink/60" data-id="element-530" data-mp="hllhj5q16ycq">
              Buying for a whole company? Custom sleeves, printed solid
              colourways and fully bespoke cups from 50 units.
            </p>
            <Link to="/corporate-branding" className="type-cta shrink-0 inline-flex items-center gap-2 text-[10px] text-pennant" data-id="element-531" data-mp="3nwtjp0o3b3g">
              Corporate &amp; branded cups
              <ArrowRightIcon className="h-3 w-3" strokeWidth={1.5} data-id="element-532" data-mp="37cgl75h9xn9o" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── SUSTAINABILITY MOMENT ────────────────────────────── */}
      <section className="w-full py-20 md:py-28" data-id="element-533" data-mp="1hvco5jcky47a">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 lg:grid-cols-2 lg:gap-20" data-id="element-534" data-mp="1hujnn047saxl">
          <div className="overflow-hidden rounded-4xl" data-id="element-535" data-mp="1moo0g005vd6l">
            <img src="https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?auto=format&fit=crop&q=80&w=1200" alt="A cup carried on a mountain trail" className="aspect-[5/4] w-full object-cover" data-id="element-536" data-mp="18w7ryjohqmya" />
          </div>
          <div data-id="element-537" data-mp="25l6ztxl5xmpy">
            <span className="mb-6 inline-flex h-11 w-11 items-center justify-center rounded-full bg-forest/10 text-forest" data-id="element-538" data-mp="25lp7dqd9znd6">
              <LeafIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-539" data-mp="1rl604lgmwlc2" />
            </span>
            <h2 className="type-display text-4xl text-coffee md:text-5xl" data-id="element-540" data-mp="1wjpo0resg772">
              The E stands for everyone.
            </h2>
            <p className="type-body mt-6 max-w-lg text-base text-ink/65" data-id="element-541" data-mp="g57lm22ot73g">
              Not eco. Everyone. A reusable cup only works if it is the one you
              actually want to pick up — so we start with the design and let
              the good habit follow. Bamboo fibre body, food-grade silicone
              lid, replaceable part by part.
            </p>
            <Link to="/about#sustainability" className="type-cta mt-8 inline-flex items-center gap-2 text-[11px] text-coffee underline decoration-oat underline-offset-[6px] hover:decoration-coffee" data-id="element-542" data-mp="2d3wtmgrhr945">
              How it is made
              <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-543" data-mp="1z5gkkj65p6iu" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── STOCKISTS / COMMUNITY ────────────────────────────── */}
      <section className="w-full bg-coffee py-20 text-paper md:py-28" data-id="element-544" data-mp="1q50j1f8z49gm">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-545" data-mp="1q6v7h3jgejpg">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20" data-id="element-546" data-mp="3oitvir8rxssx">
            <div data-id="element-547" data-mp="3ki9xzmkagn69">
              <p className="type-eyebrow mb-6 text-[11px] text-paper/55" data-id="element-548" data-mp="3kjusxdaim84n">
                Stocked nationwide
              </p>
              <h2 className="type-display text-4xl text-paper md:text-5xl" data-id="element-549" data-mp="16aik68i0004r">
                Find us at your café.
              </h2>
              <p className="type-body mt-6 max-w-md text-base text-paper/70" data-id="element-550" data-mp="1g0ty8nvebppd">
                Over forty independent cafés and roasteries carry Ecoffee Cup®
                across South Africa — and most of them will happily fill your
                own.
              </p>
              <Link to="/stockists" className="type-cta mt-8 inline-flex items-center gap-2 rounded-full border border-paper/30 px-8 py-4 text-[11px] text-paper transition-colors hover:bg-paper hover:text-coffee" data-id="element-551" data-mp="ndzi235m3chp">
                <MapPinIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-552" data-mp="11cgyrgx3noq4" />
                Find a stockist
              </Link>
            </div>
            <div className="grid gap-x-10 gap-y-8 sm:grid-cols-2" data-id="element-553" data-mp="27u431oguaqfz">
              {stockistRegions.map(r => <div key={r.id} data-id="element-554" data-mp="2b9ntb7rfwzre">
                  <p className="type-eyebrow mb-4 text-[10px] text-paper/45" data-id="element-555" data-mp="2baqhv91aiq45">
                    {r.region}
                  </p>
                  <ul className="space-y-2" data-id="element-556" data-mp="26cf5orniw3ir">
                    {r.stores.slice(0, 3).map(s => <li key={s.name} className="type-body text-sm text-paper/80" data-id="element-557" data-mp="2kbokbmw2ypkm">
                        {s.name}
                        <span className="text-paper/40" data-id="element-558" data-mp="2u6ymqbqe4yyx"> · {s.city}</span>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}